import { Effect, References, Result, Schema } from "effect";
import { IdentityObjectInput, IdentityArrayInput, Sha256Identity, UniqueIdentityValues } from "../../identity-input.ts";
import { developmentIdentifier, CommitId, type TreeId } from "../../development-identifiers.ts";
import { canonicalJsonDigest } from "../../canonical-json/digest.ts";
import { canonicalJsonStringify } from "../../canonical-json/serialize.ts";
import { canonicalJsonCause } from "../../canonical-json/cause.ts";
import type { Digest } from "../../digest/service.ts";
import { CandidateToken, CandidateReasonText, CandidateTargetRef, CandidateTargetRevision, CandidateOmissionReason, CandidateEligibilityEntry,
  CANDIDATE_MANIFEST_VERSION, CANDIDATE_ELIGIBILITY_VERSION, CANDIDATE_DECISION_VERSION, candidateManifestError,
  type CandidateManifestError, type CandidateEligibilityInput, type CandidateManifest, type CandidateTaskDecisionInput, type CandidateTaskDecision,
  type CandidateResolutionInput, type CandidateManifestInput, type CandidateManifestResult } from "./model.ts";

// Native reflection and Schema callbacks may refuse at any suspended expression.
// Keep their selected receivers across the yield and restore the typed boundary.
const boundary = <A, R>(program: Effect.Effect<A, CandidateManifestError, R>) =>
  program.pipe(Effect.catchDefect(cause => Effect.fail(candidateManifestError(cause))));
const parse = <S extends Schema.ConstraintDecoder<unknown>>(schema: S, refusal: () => unknown) => {
  const decode = Schema.decodeUnknownResult(schema);
  return (value: unknown): S["Type"] => Result.getOrThrowWith(decode(value), refusal);
};
const hash = (value: unknown) => canonicalJsonDigest(value).pipe(Effect.mapError(error => candidateManifestError(canonicalJsonCause(error))));

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

const digest = (value: unknown, label: string): string =>
  parse(Sha256Identity, () => new TypeError(`${label} must be a lowercase SHA-256 identity.`))(value);
const stableToken = (value: unknown, label: string): string =>
  parse(CandidateToken, () => new TypeError(`${label} must be a stable lowercase token.`))(value);
const boundedText = (value: unknown, label: string): string =>
  parse(CandidateReasonText, () => new TypeError(`${label} must be bounded trimmed text without controls.`))(value);
const targetRef = /* @__PURE__ */ (() => parse(CandidateTargetRef, () => new TypeError("Candidate target ref is invalid.")))();
const positiveRevision = /* @__PURE__ */ (() => parse(CandidateTargetRevision, () => new RangeError("Candidate target revision must be positive.")))();
const arrayInput = (value: unknown, label: string) =>
  parse(IdentityArrayInput, () => new TypeError(`Candidate ${label} must be an array.`))(value);
const uniqueIdentities = (values: readonly string[], label: string) =>
  parse(UniqueIdentityValues, () => new TypeError(`Candidate ${label} must be unique.`))(values);
const exactEligibleHead = /* @__PURE__ */ (() => parse(Schema.Struct({
  headCommitId: CommitId, eligible: Schema.UndefinedOr(CandidateEligibilityEntry)
}).check(Schema.makeFilter(value => value.eligible !== undefined && value.eligible.headCommitId === value.headCommitId)), () => new TypeError("Candidate decision must match one exact eligible task head.")))();
const includedDecision = /* @__PURE__ */ (() => parse(IdentityObjectInput.check(Schema.makeFilter(value =>
  !("reason" in value) && !("reasonDetail" in value)
)), () => new TypeError("Included task decisions cannot have omission reasons.")))();
const omittedDecision = /* @__PURE__ */ (() => parse(Schema.Struct({ decision: Schema.Literal("omitted"), reason: CandidateOmissionReason }), () => new TypeError("Omitted task decision reason is unsupported.")))();
const completeDecisions = /* @__PURE__ */ (() => parse(Schema.Struct({
  selected: IdentityArrayInput, eligible: IdentityArrayInput
}).check(Schema.makeFilter(value => value.selected.length === value.eligible.length &&
  value.selected.every((id, index) => id === value.eligible[index])
)), () => new TypeError("Candidate decisions must include every eligible task exactly once.")))();

function treeId(value: unknown): TreeId {
  digest(value, "Candidate base tree id");
  return developmentIdentifier("tree", value);
}

const eligibilitySnapshot = (inputs: readonly CandidateEligibilityInput[]) => boundary(Effect.gen(function* () {
  arrayInput(inputs, "eligibility");
  const entries = inputs
    .map((input) =>
      Object.freeze({
        taskId: developmentIdentifier("task", input.taskId),
        headCommitId: developmentIdentifier("commit", input.headCommitId),
        successfulRunId: developmentIdentifier("run", input.successfulRunId)
      })
    )
    .sort((left, right) => compareText(left.taskId, right.taskId));
  uniqueIdentities(entries.map(entry => entry.taskId), "eligibility task ids");
  const identity = {
    version: CANDIDATE_ELIGIBILITY_VERSION,
    entries: Object.freeze(entries)
  } as const;
  return Object.freeze({
    ...identity,
    digest: `sha256:${yield* hash(identity)}`
  });
}));

const decisionSet = (inputs: readonly CandidateTaskDecisionInput[], eligibility: CandidateManifest["eligibility"]) => boundary(Effect.gen(function* () {
  arrayInput(inputs, "decisions");
  const eligibleByTask = new Map(
    eligibility.entries.map((entry) => [entry.taskId, entry])
  );
  const entries = inputs
    .map((input): CandidateTaskDecision => {
      const taskId = developmentIdentifier("task", input.taskId);
      const headCommitId = developmentIdentifier("commit", input.headCommitId);
      const eligible = eligibleByTask.get(taskId);
      exactEligibleHead({ headCommitId, eligible });
      if (input.decision === "included") {
        includedDecision(input);
        return Object.freeze({
          version: CANDIDATE_DECISION_VERSION,
          taskId,
          headCommitId,
          decision: "included"
        });
      }
      const omitted = omittedDecision(input);
      return Object.freeze({
        version: CANDIDATE_DECISION_VERSION,
        taskId,
        headCommitId,
        decision: "omitted",
        reason: omitted.reason,
        reasonDetail: boundedText(
          input.reasonDetail,
          "Candidate omission reason detail"
        )
      });
    })
    .sort((left, right) => compareText(left.taskId, right.taskId));
  uniqueIdentities(entries.map(entry => entry.taskId), "decision task ids");
  completeDecisions({ selected: entries.map(entry => entry.taskId), eligible: eligibility.entries.map(entry => entry.taskId) });
  const identity = {
    version: CANDIDATE_DECISION_VERSION,
    entries: Object.freeze(entries)
  } as const;
  return Object.freeze({
    ...identity,
    digest: `sha256:${yield* hash(identity)}`
  });
}));

const resolutionSet = (inputs: readonly CandidateResolutionInput[]) => boundary(Effect.gen(function* () {
  arrayInput(inputs, "resolutions");
  const entries = inputs
    .map((input) =>
      Object.freeze({
        conflictId: stableToken(input.conflictId, "Candidate conflict id"),
        inputsDigest: digest(
          input.inputsDigest,
          "Candidate resolution inputs digest"
        ),
        resolutionDigest: digest(
          input.resolutionDigest,
          "Candidate resolution digest"
        )
      })
    )
    .sort((left, right) => compareText(left.conflictId, right.conflictId));
  uniqueIdentities(entries.map(entry => entry.conflictId), "resolution conflict ids");
  return Object.freeze({
    entries: Object.freeze(entries),
    digest: `sha256:${yield* hash(entries)}`
  });
}));

/** Build the immutable identity input; request/completion order is discarded. */
const creationTrace = Effect.fn("CandidateManifest.create");
export const createCandidateManifest = (input: CandidateManifestInput): Effect.Effect<CandidateManifestResult, CandidateManifestError, Digest> => creationTrace(() => boundary(Effect.gen(function* () {
  parse(IdentityObjectInput, () => new TypeError("Candidate manifest input must be an object."))(input);
  const eligibility = yield* eligibilitySnapshot(input.eligibility);
  const decisions = yield* decisionSet(input.decisions, eligibility);
  const resolutions = yield* resolutionSet(input.resolutions ?? []);
  const manifest = Object.freeze({
    version: CANDIDATE_MANIFEST_VERSION,
    changeId: developmentIdentifier("change", input.changeId),
    baseCommitId: developmentIdentifier("commit", input.baseCommitId),
    baseTreeId: treeId(input.baseTreeId),
    target: Object.freeze({
      ref: targetRef(input.targetRef),
      commitId: developmentIdentifier("commit", input.targetCommitId),
      revision: positiveRevision(input.targetRevision)
    }),
    eligibility,
    decisions,
    resolutions: resolutions.entries,
    resolutionsDigest: resolutions.digest,
    compositionAlgorithm: stableToken(
      input.compositionAlgorithm,
      "Candidate composition algorithm"
    ),
    sourcePolicyDigest: digest(
      input.sourcePolicyDigest,
      "Candidate source policy digest"
    )
  }) satisfies CandidateManifest;
  const canonicalJson = yield* Effect.fromResult(canonicalJsonStringify(manifest)).pipe(Effect.mapError(error => candidateManifestError(canonicalJsonCause(error))));
  const manifestDigest = `sha256:${yield* hash(manifest)}`;
  return Object.freeze({
    candidateId: developmentIdentifier("candidate", manifestDigest),
    manifestDigest,
    manifest,
    canonicalJson
  });
})))().pipe(Effect.uninterruptible, Effect.provideService(References.PreventSchedulerYield, true));
